import React, { useMemo, Fragment } from "react";
import { get, upperFirst, some } from "lodash";
import { RaceCardRunner, RaceTypeCodeEnum } from "@tvg/ts-types/Race";
import useLocalStorage from "@tvg/utils/useLocalStorage";
import { Icon } from "@tvg/design-system";
import type { HandicappingRunnerFlags } from "@tvg/ipp/src/components/RunnerFlags";
import RunnerFlags from "@tvg/ipp/src/components/RunnerFlags";
import {
  RunnerInfoContainer,
  RunnerInfoHeader,
  RunnerInfoTitle,
  RunnerInfoShowMoreLink,
  RunnerMainInfo,
  RunnerFlagsContainer,
  RunnerComplementaryInfo,
  ComplementaryInfo,
  ComplementaryInfoLabel,
  ComplementaryInfoValue,
  IconContainer,
  IconWrapper
} from "./styled-components";

interface Props {
  title: string;
  runner: RaceCardRunner;
  raceTypeCode: RaceTypeCodeEnum;
  currentRaceTypeCode: RaceTypeCodeEnum;
  qaLabel: string;
  children: JSX.Element;
  handicappingRunnerFlags: HandicappingRunnerFlags;
}

const RUNNER_INFO_SHOW_EXPANDED_KEY = "ppRunnerInfoExpanded";

const RunnerInfo = (props: Props) => {
  const [showMoreInfo, setShowMoreInfo] = useLocalStorage(
    RUNNER_INFO_SHOW_EXPANDED_KEY,
    true
  );
  const mainInfo = ["age", "sex", "med", "weight"];
  const complementaryInfo = [
    "jockey",
    "trainer",
    "ownerName",
    "sire",
    "dam",
    "damSire"
  ];
  const complementaryInfoKeyChanges =
    props.runner && props.runner.hasJockeyChanges ? ["jockey"] : [];
  const renderRunnerFlags = () => {
    const flags = get(props, "runner.timeform.flags", {});
    const hasFlags = some(flags, (flag) => !!flag);
    return (
      hasFlags && (
        <RunnerFlagsContainer>
          <RunnerFlags
            runnerId={props.runner.runnerId}
            runnerName={props.runner.horseName}
            flags={flags}
            handicappingRunnerFlags={props.handicappingRunnerFlags}
            helpShown
          />
        </RunnerFlagsContainer>
      )
    );
  };

  return useMemo(
    () =>
      props.runner && (
        <RunnerInfoContainer data-qa-label={props.qaLabel}>
          <RunnerInfoHeader>
            <RunnerInfoTitle>{props.title}</RunnerInfoTitle>
            <RunnerInfoShowMoreLink
              onClick={() => setShowMoreInfo(!showMoreInfo)}
              data-qa-label={`${props.qaLabel}-show-more`}
            >
              {`${showMoreInfo ? "Hide" : "Show"} Horse Info`}
            </RunnerInfoShowMoreLink>
          </RunnerInfoHeader>
          <RunnerMainInfo data-qa-label={`${props.qaLabel}-main`}>
            <>
              {mainInfo
                .map((infoKey) => {
                  const runnerInfo =
                    props.runner[infoKey as keyof RaceCardRunner] || "-";
                  return (
                    (infoKey === "age" && `${runnerInfo} year old`) ||
                    (infoKey === "weight" && `${runnerInfo} lbs`) ||
                    runnerInfo
                  );
                })
                .join(", ")}
              {renderRunnerFlags()}
            </>
          </RunnerMainInfo>
          <RunnerComplementaryInfo
            isVisible={showMoreInfo}
            data-qa-label={`${props.qaLabel}-complementary`}
          >
            {complementaryInfo.map((infoKey) => {
              const hasChanges = complementaryInfoKeyChanges.includes(infoKey);
              return (
                <ComplementaryInfo
                  key={infoKey.replace(/\s/g, "")}
                  data-qa-label={`${props.qaLabel}-complementary-${infoKey}`}
                >
                  <ComplementaryInfoLabel
                    data-qa-label={`${props.qaLabel}-complementary-${infoKey}-label`}
                  >
                    {upperFirst(
                      infoKey
                        .replace("ownerName", "owner")
                        .replace("jockey", (match) =>
                          props.currentRaceTypeCode === "H" ? "driver" : match
                        )
                    )}
                  </ComplementaryInfoLabel>
                  {hasChanges && (
                    <IconContainer>
                      <IconWrapper>
                        <Icon
                          name="changes"
                          size="s"
                          lineColor="orange.400"
                          backgroundColor="transparent"
                        />
                      </IconWrapper>
                    </IconContainer>
                  )}
                  <ComplementaryInfoValue
                    data-qa-label={`${props.qaLabel}-complementary-${infoKey}-value`}
                    hasChanges={hasChanges}
                  >
                    {props.runner[infoKey as keyof RaceCardRunner] || "-"}
                  </ComplementaryInfoValue>
                </ComplementaryInfo>
              );
            })}
            {props.children && (
              <RunnerComplementaryInfo smallPadding isVisible>
                {props.children}
              </RunnerComplementaryInfo>
            )}
          </RunnerComplementaryInfo>
        </RunnerInfoContainer>
      ),
    [JSON.stringify(props.runner), showMoreInfo]
  );
};

RunnerInfo.defaultProps = {
  title: "",
  raceTypeCode: "T",
  children: null,
  qaLabel: "runner-info",
  handicappingRunnerFlags: {
    title: "",
    subtitle: "",
    subtitleRunnerFlags: "",
    flags: []
  },
  currentRaceTypeCode: "ALL"
};

export default RunnerInfo;
